import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Wizard from '../../containers/Modal/Wizard';
import { SelectField } from '../../containers/Form';
import { notificationActions } from '../../components/Notification/redux/actions';
import {
  getRoleSwitchingMode,
  getSwitchableRoles,
  getCurrentRole,
  switchRole,
  resetToNativeRole,
  ALL_FACILITIES_UUID,
  userProfile,
  addRoleToRecentlySwitched,
} from '../../actions/auth';
import Strings from '../../Strings';
import './RoleSwitcherModal.scss';
import { isDevelopment } from '../../utils/environment';

export const ROLE_SWITCHER_MODAL_NAME = 'role-switcher';

function RoleSwitcherModal(props) {
  const {
    fetchMode,
    fetchSwitchable,
    fetchCurrentRole,
    onSwitchRole,
    onResetRole,
    getUserProfile,
    showNotification,
    saveRoleSwitch,
    recentlySwitchedRoles,
  } = props;

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [highlightInvalidFields, setHighlightInvalidFields] = useState(false);

  // Data from API
  const [switchableOrgs, setSwitchableOrgs] = useState([]);
  const [currentRole, setCurrentRole] = useState(null);
  const [originalRole, setOriginalRole] = useState(null);

  // Selected values
  const [selectedOrgUuid, setSelectedOrgUuid] = useState('');
  const [selectedRoleUuid, setSelectedRoleUuid] = useState('');
  const [selectedFacilityUuid, setSelectedFacilityUuid] = useState(ALL_FACILITIES_UUID);


  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Fetch switchable roles and current role in parallel
        const [switchableResponse, currentRoleResponse] = await Promise.all([
          fetchSwitchable(),
          fetchCurrentRole(),
        ]);

        const orgs = switchableResponse?.response || [];
        setSwitchableOrgs(orgs);

        const current = currentRoleResponse?.response?.current_role;
        const original = currentRoleResponse?.response?.original_role;
        setCurrentRole(current);
        setOriginalRole(original);

        // Initialize selections based on current role
        if (current) {
          setSelectedOrgUuid(current.org_uuid || '');
          setSelectedRoleUuid(current.role_uuid || '');
        }
        setSelectedFacilityUuid(ALL_FACILITIES_UUID);
      } catch (error) {
        console.error('Error loading role switching data:', error);
        showNotification(Strings.roleSwitcher.errorLoading, 5000, true);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const selectedOrg = switchableOrgs.find(o => o.org_uuid === selectedOrgUuid);
  const availableRoles = selectedOrg?.roles || [];
  const availableFacilities = selectedOrg?.facilities || [];


  // For admin/site modes, auto-select the org
  useEffect(() => {
    if (switchableOrgs.length > 0 && !showOrgDropdown && !selectedOrgUuid) {
      const nativeOrg = switchableOrgs.find(o => o.is_native) || switchableOrgs[0];
      if (nativeOrg) {
        setSelectedOrgUuid(nativeOrg.org_uuid);
      }
    }
  }, [switchableOrgs]);

  // Reset role selection when org changes
  useEffect(() => {
    if (selectedOrgUuid && availableRoles.length > 0) {
      const stillValid = availableRoles.some(r => r.role_uuid === selectedRoleUuid);
      if (!stillValid) {
        // Try to select native role first
        const nativeRole = availableRoles.find(r => r.is_native);
        setSelectedRoleUuid(nativeRole?.role_uuid || availableRoles[0]?.role_uuid || '');
      }
    }
  }, [selectedOrgUuid]);

  // Reset facility selection when org changes
  useEffect(() => {
    if (selectedOrgUuid && availableFacilities.length > 0) {
      const stillValid = availableFacilities.some(f => f.facility_uuid === selectedFacilityUuid);
      if (!stillValid) {
        const allFacility = availableFacilities.find(f => f.is_all);
        setSelectedFacilityUuid(allFacility?.facility_uuid || availableFacilities[0]?.facility_uuid || ALL_FACILITIES_UUID);
      }
    }
  }, [selectedOrgUuid]);

  // Derived values
  const showOrgDropdown = switchableOrgs.length > 1;
  const showFacilityDropdown = false;
  const showRoleDropdown = availableRoles.length > 0;

  const isNativeRole = currentRole && originalRole
    ? currentRole.role_uuid === originalRole.role_uuid && currentRole.org_uuid === originalRole.org_uuid
    : true;

  const canSubmit = selectedOrgUuid && (!showRoleDropdown || selectedRoleUuid);

  // Handle role switch
  const handleSwitchRole = async (orgUuid, roleUuid, facilityUuid, orgName, roleName) => {
    if (isSaving) return;

    if (!orgUuid) {
      orgUuid = selectedOrgUuid;
    }
    if (!roleUuid) {
      roleUuid = selectedRoleUuid;
    }
    if (showFacilityDropdown && !facilityUuid) {
      facilityUuid = selectedFacilityUuid;
    }
    setIsSaving(true);
    try {
      const response = await onSwitchRole({
        roleUuid,
        orgUuid,
        facilityUuid,
      });

      if (response?.type?.includes('error')) {
        const errorMsg = response?.response?.data?.standard_error?.message
          || response?.response?.data?.error_msg
          || Strings.roleSwitcher.errorSwitching;
        showNotification(errorMsg, 5000, true);
        setIsSaving(false);
      } else {
        // Refresh user profile and redirect to root - App will redirect to appropriate default page
        const switchedRoleInfo = {
          role_uuid: roleUuid,
          role_name: availableRoles.find(r => r.role_uuid === roleUuid)?.role_title || roleName || roleUuid,
          org_uuid: orgUuid,
          org_name: switchableOrgs.find(o => o.org_uuid === orgUuid)?.org_name || orgName || orgUuid,
          facility_uuid: facilityUuid,
        };
        if (isDevelopment()) {
          saveRoleSwitch(switchedRoleInfo);
        }
        await getUserProfile();
        window.location.replace('/');
      }
    } catch (error) {
      console.error('Error switching role:', error);
      showNotification(Strings.roleSwitcher.errorSwitching, 5000, true);
      setIsSaving(false);
    }
  };

  // Handle reset to native role
  const handleResetRole = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const response = await onResetRole();

      if (response?.type?.includes('error')) {
        const errorMsg = response?.response?.data?.standard_error?.message
          || response?.response?.data?.error_msg
          || Strings.roleSwitcher.errorResetting;
        showNotification(errorMsg, 5000, true);
        setIsSaving(false);
      } else {
        // Success - reload the page
        await getUserProfile();
        window.location.replace('/');
      }
    } catch (error) {
      console.error('Error resetting role:', error);
      showNotification(Strings.roleSwitcher.errorResetting, 5000, true);
      setIsSaving(false);
    }
  };


  const recentlySwitchedContent = isDevelopment() && recentlySwitchedRoles?.length > 0 ? (
    <>
      <span className="label">Recently used:</span>
      {recentlySwitchedRoles.map(role => {
        const org = switchableOrgs.find(o => o.org_uuid === role.org_uuid);
        const roleInfo = org?.roles.find(r => r.role_uuid === role.role_uuid);
        const orgName = org?.org_name || role.org_name || role.org_uuid;
        const roleName = roleInfo?.role_title || role.role_name || role.role_uuid;
        return (
          <div
            role="button"
            data-testid={`recently-switched-${orgName}-${roleName}`}
            key={`${role.org_uuid}-${role.role_uuid}`}
            className="recently-switched-role"
            onClick={() => {
              handleSwitchRole(role.org_uuid, role.role_uuid, role.facility_uuid, orgName, roleName);
            }}
          >
            <span className="role-name">{roleName}</span>
            <span className="org-name">@ {orgName}</span>
            <span> ({role.switchCount} times switched)</span>
          </div>
        )
      })}
    </>
  ) : null;
  const currentRoleInfo = (
    <div className="current-role-info">
      <span className="label">{Strings.roleSwitcher.currentRole}:</span>
      {!currentRole ? (
        <div className="spinner" />
      ) : (
        <>
          <span className="role-name">{currentRole?.role_title || ''}</span>
          {currentRole?.org_name && (
            <span className="org-name">@ {currentRole?.org_name}</span>
          )}
          {!isNativeRole && (
            <span className="switched-badge">{Strings.roleSwitcher.switched}</span>
          )}
        </>
      )}
    </div>    
  );

  // Build the content
  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          {currentRoleInfo}
          {recentlySwitchedContent}
          <div className="spinner-container">
            <div className="spinner" />
          </div>
        </>
      );
    }

    if (switchableOrgs.length === 0) {
      return (
        <div className="empty-row-container">
          <div className="empty-row-inner">{Strings.roleSwitcher.noRolesAvailable}</div>
        </div>
      );
    }

    return (
      <>
        {currentRoleInfo}
        {recentlySwitchedContent}
        {/* Organization dropdown */}
        {showOrgDropdown && (
          <SelectField
            name="organization"
            id="role-switcher-org"
            label={Strings.organization}
            value={selectedOrgUuid}
            onChange={option => setSelectedOrgUuid(option?.value || '')}
            data={switchableOrgs.map(org => ({
              label: org.org_name,
              value: org.org_uuid,
            }))}
            disabled={isSaving}
            isSearchable
            isRequired
            highlightInvalid
          />
        )}

        {/* Facility dropdown */}
        {showFacilityDropdown && availableFacilities.length > 0 && (
          <SelectField
            name="facility"
            id="role-switcher-facility"
            label={Strings.facility}
            value={selectedFacilityUuid}
            onChange={option => setSelectedFacilityUuid(option?.value || ALL_FACILITIES_UUID)}
            data={availableFacilities.map(facility => ({
              label: facility.is_all ? Strings.roleSwitcher.allFacilities : facility.facility_name,
              value: facility.facility_uuid,
            }))}
            disabled={!selectedOrgUuid || isSaving}
            isRequired
            highlightInvalid={highlightInvalidFields}
          />
        )}

        {/* Role dropdown */}
        {showRoleDropdown && (
          <SelectField
            name="role"
            id="role-switcher-role"
            label={Strings.role}
            value={selectedRoleUuid}
            onChange={option => setSelectedRoleUuid(option?.value || '')}
            data={availableRoles.map(role => ({
              label: role.role_title,
              value: role.role_uuid,
            }))}
            disabled={!selectedOrgUuid || isSaving}
            isRequired
            highlightInvalid={highlightInvalidFields}
          />
        )}

        {/* Reset button */}
        {!isNativeRole && (
          <div className="reset-button-container">
            <button
              type="button"
              data-testid="reset-to-native-role-button"
              className="brand-white-gray"
              onClick={handleResetRole}
              disabled={isSaving}
              style={{ width: '100%' }}
            >
              {isSaving ? Strings.roleSwitcher.resetting : Strings.roleSwitcher.resetToNative}
            </button>
          </div>
        )}
      </>
    );
  };

  const pages = [
    {
      id: 'role-switcher',
      title: Strings.roleSwitcher.title,
      content: renderContent(),
      canGoNext: canSubmit && !isLoading,
      nextButton: {
        text: isSaving ? Strings.roleSwitcher.switching : Strings.roleSwitcher.switchRole,
      },
    },
  ];

  return (
    <Wizard
      name={ROLE_SWITCHER_MODAL_NAME}
      className="role-switcher"
      pages={pages}
      onSubmit={handleSwitchRole}
      showPagesFilter={false}
      onNextButtonHover={e => setHighlightInvalidFields(e)}
    />
  );
}

RoleSwitcherModal.propTypes = {
  fetchMode: PropTypes.func.isRequired,
  fetchSwitchable: PropTypes.func.isRequired,
  fetchCurrentRole: PropTypes.func.isRequired,
  onSwitchRole: PropTypes.func.isRequired,
  onResetRole: PropTypes.func.isRequired,
  saveRoleSwitch: PropTypes.func.isRequired,
  getUserProfile: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  fetchMode: () => dispatch(getRoleSwitchingMode()),
  fetchSwitchable: () => dispatch(getSwitchableRoles()),
  fetchCurrentRole: () => dispatch(getCurrentRole()),
  onSwitchRole: params => dispatch(switchRole(params)),
  onResetRole: () => dispatch(resetToNativeRole()),
  saveRoleSwitch: roleInfo => dispatch(addRoleToRecentlySwitched(roleInfo)),
  getUserProfile: () => dispatch(userProfile()),
  showNotification: (message, timeout, isError) =>
    dispatch(notificationActions.show(message, timeout, isError)),
});

const mapStateToProps = state => ({
  recentlySwitchedRoles: state.auth.recentlySwitchedRoles,
});

export default connect(mapStateToProps, mapDispatchToProps)(RoleSwitcherModal);
